import React, { useEffect, useRef, useState } from "react";
import { Box } from "@material-ui/core";
import { ShapeEditor, ImageLayer, DrawLayer } from "react-shape-editor";
import { useAnnotationPainter, useAnnotationsHandler } from "pages/MaterialSpecs/hooks";
import { MIN_ZOOM, SCALE_FACTOR } from "../..";
import { LoadingOverlay } from "components";

interface ImagePreviewProps {
    path: string;
}

export const ImagePreview = ({ path }: ImagePreviewProps) => {
    const [isImageLoading, setImageLoading] = useState(true);
    const container = useRef<HTMLDivElement>(null);
    const [imageDimensions, setImageDimensions] = useState<{ height: number; width: number }>({
        height: 0,
        width: 0,
    });
    const { scale, changeScale } = useAnnotationsHandler();
    const { drawHandler, shapes, addShapeHandler } = useAnnotationPainter();

    useEffect(() => {
        // when URL changes, show overlay
        setImageLoading(true);
    }, [path]);

    // scales image on open, if too big to fit the container
    useEffect(() => {
        const { width, height } = imageDimensions;

        if (width !== 0 && height !== 0 && container.current) {
            const { offsetWidth, offsetHeight } = container.current;
            let newWidth = width;
            let newHeight = height;
            let newScale = scale;

            while ((newWidth > offsetWidth || newHeight > offsetHeight) && newScale > MIN_ZOOM) {
                newScale -= SCALE_FACTOR;
                newWidth = width * newScale;
                newHeight = height * newScale;
            }

            if (newScale !== scale) {
                changeScale(newScale);
            }
        }

        // eslint-disable-next-line
    }, [imageDimensions, changeScale]);

    return (
        <Box overflow="auto" height="100%" {...{ ref: container }}>
            <Box
                display="flex"
                flex={1}
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%">
                <LoadingOverlay active={isImageLoading}>
                    <ShapeEditor
                        vectorWidth={imageDimensions.width}
                        vectorHeight={imageDimensions.height}
                        scale={scale}>
                        <ImageLayer
                            src={path}
                            onLoad={({ naturalWidth, naturalHeight }) => {
                                setImageLoading(false);
                                setImageDimensions({
                                    width: naturalWidth,
                                    height: naturalHeight,
                                });
                            }}
                        />
                        <DrawLayer
                            onDrawStart={({ startCorner }) => drawHandler(startCorner)}
                            onAddShape={addShapeHandler}
                        />
                        {shapes}
                    </ShapeEditor>
                </LoadingOverlay>
            </Box>
        </Box>
    );
};
